/**
 * /reconcile — Reconcile hub page. Thin shell that wires `useReconcile`
 * (all state + side effects) into three pure view chunks plus the
 * supporting modal stack.
 *
 * Split from the original 674-line single-file implementation per the
 * pattern in components/CONTRIBUTING.md.
 *
 *   pages/Reconciliation.js                  ← this shell, ~110 lines
 *   pages/reconciliation/
 *     useReconcile.js                        ← state + side effects
 *     ReconcileHeader.jsx                    ← title + Upload statement + ExportMenu
 *     ReconcileFilters.jsx                   ← month/year + account + tag pills
 *     ReconcileTable.jsx                     ← table + empty state (incl. SortableTh)
 *     constants.js                           ← TAG_FILTERS / MONTHS / YEARS / todayIso
 *
 * FUTURE: "Account balancing" cards (Statement / App / Difference) and
 * the bank-statement auto-reconcile dialog are deliberately not on the
 * page. Backend endpoints /api/reconcile/match and /api/reconcile/apply
 * remain intact; the AutoReconcileDialog is mounted here (Steel-gated)
 * so the Upload statement entry point flows through it.
 */
import React from 'react';
import { Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasTier } from '../lib/tier';
import ConfirmAmountDialog from '../components/ConfirmAmountDialog';
import AutoReconcileDialog from '../components/AutoReconcileDialog';
import TierGate from '../components/TierGate';
import { useReconcile } from './reconciliation/useReconcile';
import ReconcileHeader from './reconciliation/ReconcileHeader';
import ReconcileFilters from './reconciliation/ReconcileFilters';
import ReconcileTable from './reconciliation/ReconcileTable';

const Reconciliation = () => {
  const { user } = useAuth();
  const isAutoReconcileUnlocked = hasTier(user, 'pro');
  const r = useReconcile();

  if (r.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="reconciliation-page">
      <ReconcileHeader
        isAutoReconcileUnlocked={isAutoReconcileUnlocked}
        onUploadClick={() => {
          if (isAutoReconcileUnlocked) r.setAutoReconcileOpen(true);
          else r.setAutoReconcileUpsellOpen(true);
        }}
        year={r.year}
        month={r.month}
        rows={r.rows}
        accountNameMap={r.accountNameMap}
        accountKindMap={r.accountKindMap}
      />

      <ReconcileFilters
        year={r.year}
        month={r.month}
        setYear={r.setYear}
        setMonth={r.setMonth}
        stepMonth={r.stepMonth}
        accounts={r.accounts}
        accountFilter={r.accountFilter}
        setAccountFilter={r.setAccountFilter}
        tagFilter={r.tagFilter}
        setTagFilter={r.setTagFilter}
        selectedKeysSize={r.selectedKeys.size}
        bulkReconcile={r.bulkReconcile}
        busy={r.busy}
      />

      <ReconcileTable
        rows={r.rows}
        year={r.year}
        month={r.month}
        sortConfig={r.sortConfig}
        toggleSort={r.toggleSort}
        selectedKeys={r.selectedKeys}
        bulkEligibleKeys={r.bulkEligibleKeys}
        toggleSelect={r.toggleSelect}
        toggleSelectAll={r.toggleSelectAll}
        accountNameMap={r.accountNameMap}
        accountKindMap={r.accountKindMap}
        busy={r.busy}
        handleRowAction={r.handleRowAction}
      />

      <ConfirmAmountDialog
        item={r.confirmTarget}
        open={!!r.confirmTarget}
        onOpenChange={(open) => { if (!open) r.setConfirmTarget(null); }}
        onConfirm={r.handleConfirmAmount}
      />

      <AutoReconcileDialog
        open={r.autoReconcileOpen}
        onOpenChange={r.setAutoReconcileOpen}
        accounts={r.accounts}
        onApplied={r.fetchData}
      />

      <TierGate.Dialog
        open={r.autoReconcileUpsellOpen}
        onOpenChange={r.setAutoReconcileUpsellOpen}
        detail={{
          required_tier: 'pro',
          current_tier: user?.tier || 'free',
          message: "Auto-reconcile from a bank statement is a Steel feature. Upload a CSV or Excel and Forge Ledger matches every row against your transactions automatically.",
          upgrade_url: '/pricing',
        }}
        title="Match bank statements automatically"
        bullets={[
          'Upload a CSV or Excel from any UK bank',
          'Smart match by amount (±£0.01) + date (±5 days) + description',
          'Review confident matches with a one-click apply',
          'Catch up on months of reconciliation in under a minute',
        ]}
        icon={Upload}
        testIdPrefix="auto-reconcile-upsell"
      />
    </div>
  );
};

export default Reconciliation;
